import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { DateUtil } from './DateUtil';
import { ProjectPhase } from './model/general/ProjectPhase';
import {
  conStages,
  devStages,
  getStageIndex,
  isConstructionStage,
  isDevelopmentStage,
  ProjectStage,
  stageToLabel,
} from './model/general/ProjectStage';
import { CommonMilestoneDef } from './model/milestones/CommonMilestoneDef';
import { CountryMilestoneDef } from './model/milestones/CountryMilestoneDef';
import { GovernanceGroup } from './model/milestones/GovernanceGroup';
import { Milestone } from './model/milestones/Milestone';
import { getCategoryIndex, MilestoneCategory } from './model/milestones/MilestoneCategory';
import { MilestoneNaStatus } from './model/milestones/MilestoneNaStatus';
import { AbstractMilestone } from './services/milestones/AbstractMilestone';
import { RichMilestone } from './services/milestones/RichMilestone';
import { ScheduleData } from './services/milestones/ScheduleData';
import { StageData } from './services/milestones/StageData';
import { StageMatrixData } from './services/milestones/StageMatrixData';
import { MilestoneDefinitions } from './services/milestones/MilestoneDefinitions';
import { MilestoneData } from './services/milestones/MilestoneData';
import { ConstructionMilestone } from './model/construction/ConstructionMilestone';
import { ConstructionMilestoneId, idToStage } from './model/construction/ConstructionMilestoneId';
import { MandatoryMilestonesMap, MmsTemplate } from '../reports-area/table-report/Milestones';
import { RichMilestoneData } from './services/milestones/RichMilestoneData';
import { CountryMilestoneDependencyEntityType } from './model/milestones/CountryMilestoneDependencyEntityType';
import { isAppliedInDigiRES } from './model/milestones/CountryMilestoneDependencyAppliedInApplication';
import { MilestoneDependencyUtil } from './MilestoneDependencyUtil';
import { MilestoneCalcContext } from './MilestoneCalcContext';
import { GetProjects } from './services/project-list/GetProjects';
import { Technology } from './model/general/Technology';
import { BusinessModel, isDivestment } from './model/general/BusinessModel';
import { ValidatorUtil } from './ValidatorUtil';

export class MilestoneUtil {
  public static transformDevMs(milestone: Milestone): AbstractMilestone {
    return {
      countryDefId: milestone.countryDefId,
      commonDefId: milestone.commonDefId,
      date: milestone.date,
      done: milestone.done,
      na: milestone.naStatus === MilestoneNaStatus.NOT_APPLICABLE,
    };
  }

  public static transformAllDevMs(milestones: Milestone[]): AbstractMilestone[] {
    return milestones.map((ms) => this.transformDevMs(ms));
  }

  public static transformConsMs(milestone: ConstructionMilestone): AbstractMilestone {
    return {
      countryDefId: milestone.id,
      commonDefId: milestone.id,
      date: milestone.date,
      done: milestone.done,
      na: false,
      dateStart: milestone.startDate,
      dateFid: milestone.fidDate,
      dateFidStart: milestone.fidStartDate,
      started: milestone.done || milestone.started,
    };
  }

  public static transformAllConsMs(milestones: ConstructionMilestone[]): AbstractMilestone[] {
    return milestones.filter((ms) => MandatoryMilestonesMap.has(ms.id)).map((ms) => this.transformConsMs(ms));
  }

  public static getMilestoneNaStatus(
    countryMilestoneDef: CountryMilestoneDef,
    notApplicable: boolean,
  ): MilestoneNaStatus {
    return countryMilestoneDef?.notApplicable
      ? notApplicable
        ? MilestoneNaStatus.NOT_APPLICABLE
        : MilestoneNaStatus.APPLICABLE
      : MilestoneNaStatus.ALWAYS_APPLICABLE;
  }

  public static isIncompleteInPast(milestone: { date: Date; done: boolean; na: boolean }): boolean {
    if (milestone.na || !milestone.date) {
      return false;
    }
    if (milestone.date && !milestone.done) {
      const now: number = Date.now();
      return new Date(milestone.date).getTime() < now;
    }
    return false;
  }

  public static isCompleteInFuture(milestone: { date: Date; done: boolean; na: boolean }): boolean {
    if (milestone.na || !milestone.date) {
      return false;
    }
    if (milestone.date && milestone.done) {
      const now: number = Date.now();
      return new Date(milestone.date).getTime() > now;
    }
    return false;
  }

  public static createMsCalcContext(
    phase: ProjectPhase,
    leadingTech: Technology,
    technologies: Technology[],
    primaryBusinessModel: BusinessModel,
  ): MilestoneCalcContext {
    return {
      phase: phase,
      businessModel: primaryBusinessModel,
      leadingTechnology: leadingTech,
      technologies: technologies,
    };
  }

  public static createMsCalcContextFromDto(project: GetProjects): MilestoneCalcContext {
    return {
      phase: project.phase,
      businessModel: project.primaryBusinessModel,
      leadingTechnology: project.leadingTechnology,
      technologies: project.technologies,
    };
  }

  public static calculateRichMilestones(
    ctxt: MilestoneCalcContext,
    milestones: AbstractMilestone[],
    selections: { [selectorId: string]: string },
    milestoneDefinitions: MilestoneDefinitions,
    governanceGroups: GovernanceGroup[],
  ): RichMilestoneData {
    if (milestoneDefinitions.commonMilestones.length <= 0 || milestoneDefinitions.countryMilestones.length <= 0) {
      return {
        stages: [],
        categories: [],
        allMilestones: [],
        countryDefIdToMilestone: {},
        stageAndCategoryToMilestones: {},
        dependentMilestoneSync: [],
      };
    }
    const filteredCountryMilestoneDefs = this.filterForDefsOnScheduleTab(milestoneDefinitions.countryMilestones);

    const filteringResults = this.getMilestonesFiltered(
      ctxt,
      milestones,
      selections,
      milestoneDefinitions,
      governanceGroups,
    );
    const projectMilestones: AbstractMilestone[] = filteringResults.ms;
    const filteredMilestones: Set<string> = filteringResults.filtered;

    const milestonesData: RichMilestoneData = {
      stages: [],
      categories: [],
      allMilestones: [],
      countryDefIdToMilestone: {},
      stageAndCategoryToMilestones: {},
      dependentMilestoneSync: [],
    };

    for (const msDef of filteredCountryMilestoneDefs) {
      if (filteredMilestones.has(msDef.id) || (msDef.commonId && filteredMilestones.has(msDef.commonId))) {
        continue;
      }
      const commonMsDef = milestoneDefinitions.commonMilestonesMap[msDef.commonId];
      this.addMilestoneToData(milestonesData, commonMsDef, msDef, projectMilestones);
    }
    if (ctxt.phase !== ProjectPhase.DEVELOPMENT) {
      for (const constructionId of Object.values(ConstructionMilestoneId)) {
        this.addConstructionMilestoneToData(constructionId, milestonesData, projectMilestones);
      }
      this.filterDuplicateCod(milestonesData);
    }
    this.sortMilestones(milestonesData.allMilestones);

    this.calculateDependentMilestones(milestonesData);

    milestonesData.dependentMilestoneSync = MilestoneDependencyUtil.calculateDependencyMilestones(
      milestonesData.allMilestones,
    );
    for (const depSync of milestonesData.dependentMilestoneSync) {
      depSync.milestoneData.date = depSync.nextDate;
      depSync.milestoneData.done = depSync.nextDone;
    }

    return milestonesData;
  }

  public static calculateStageData(
    ctxt: MilestoneCalcContext,
    milestones: AbstractMilestone[],
    selections: { [selectorId: string]: string },
    milestoneDefinitions: MilestoneDefinitions,
    governanceGroups: GovernanceGroup[],
  ): StageData {
    const richMilestonesData = this.calculateRichMilestones(
      ctxt,
      milestones,
      selections,
      milestoneDefinitions,
      governanceGroups,
    );
    const stageData: StageData = {
      currentStage: null,
      dependentMilestoneSync: richMilestonesData.dependentMilestoneSync,
      stages: richMilestonesData.stages,
      categories: richMilestonesData.categories,
      allMilestones: richMilestonesData.allMilestones,
      countryDefIdToMilestone: richMilestonesData.countryDefIdToMilestone,
      stageAndCategoryToMilestones: richMilestonesData.stageAndCategoryToMilestones,
    };
    stageData.stages.sort((a, b) => getStageIndex(a) - getStageIndex(b));
    stageData.categories.sort((a, b) => getCategoryIndex(a) - getCategoryIndex(b));

    stageData.currentStage = this.calculateCurrentStage(ctxt.phase, stageData);

    return stageData;
  }

  private static filterDuplicateCod(stageData: RichMilestoneData) {
    const developmentCod = stageData.allMilestones.find((ms) => !ms.construction && ms.id.endsWith('_cod'));
    const constructionCod = stageData.countryDefIdToMilestone[ConstructionMilestoneId.cod];
    if (!developmentCod || !constructionCod) {
      return;
    }
    const indexInAll = stageData.allMilestones.indexOf(developmentCod);
    stageData.allMilestones.splice(indexInAll, 1);

    delete stageData.countryDefIdToMilestone[developmentCod.id];

    const stageCategoryList = stageData.stageAndCategoryToMilestones[developmentCod.stage][developmentCod.category];
    const indexInStageCat = stageCategoryList.indexOf(developmentCod);
    stageCategoryList.splice(indexInStageCat, 1);

    constructionCod.customRef = developmentCod.customRef;
    constructionCod.category = developmentCod.category;
    constructionCod.stage = developmentCod.stage;
  }

  public static calculateStageMatrixData(
    ctxt: MilestoneCalcContext,
    milestones: AbstractMilestone[],
    selections: { [selectorId: string]: string },
    milestoneDefinitions: MilestoneDefinitions,
    governanceGroups: GovernanceGroup[],
  ): StageMatrixData {
    const stageData = this.calculateStageData(ctxt, milestones, selections, milestoneDefinitions, governanceGroups);
    const matrixData: StageMatrixData = {
      currentStage: stageData.currentStage,
      dependentMilestoneSync: stageData.dependentMilestoneSync,
      stages: stageData.stages,
      categories: stageData.categories,
      allMilestones: stageData.allMilestones,
      countryDefIdToMilestone: stageData.countryDefIdToMilestone,
      stageAndCategoryToMilestones: stageData.stageAndCategoryToMilestones,

      matrixStages: [],
      matrixCategories: [],
      milestoneMatrix: {},

      completionByCategory: {},
      completionTotal: { total: 0, completed: 0, percent: '0%' },
    };
    if (stageData.allMilestones.length <= 0) {
      return matrixData;
    }

    for (const ms of stageData.allMilestones) {
      if (!matrixData.milestoneMatrix[ms.stage]) {
        matrixData.milestoneMatrix[ms.stage] = {};
      }
      if (!matrixData.milestoneMatrix[ms.stage][ms.category]) {
        matrixData.milestoneMatrix[ms.stage][ms.category] = [];
      }
      if (ms.countryDef.matrixTab) {
        this.addIfAbsent(matrixData.matrixStages, ms.stage);
        this.addIfAbsent(matrixData.matrixCategories, ms.category);
        matrixData.milestoneMatrix[ms.stage][ms.category].push(ms);
      }
    }
    matrixData.matrixStages.sort((a, b) => getStageIndex(a) - getStageIndex(b));
    matrixData.matrixCategories.sort((a, b) => getCategoryIndex(a) - getCategoryIndex(b));
    this.calculateCompletion(matrixData);

    return matrixData;
  }

  public static calculateScheduleData(
    ctxt: MilestoneCalcContext,
    milestones: AbstractMilestone[],
    selections: { [selectorId: string]: string },
    milestoneDefinitions: MilestoneDefinitions,
    governanceGroups: GovernanceGroup[],
  ): ScheduleData {
    const stageData = this.calculateStageData(ctxt, milestones, selections, milestoneDefinitions, governanceGroups);
    const scheduleData: ScheduleData = {
      currentStage: stageData.currentStage,
      stages: stageData.stages,
      categories: stageData.categories,
      allMilestones: stageData.allMilestones,
      countryDefIdToMilestone: stageData.countryDefIdToMilestone,
      stageAndCategoryToMilestones: stageData.stageAndCategoryToMilestones,
      dependentMilestoneSync: stageData.dependentMilestoneSync,

      [ProjectStage.ORIGINATION]: null,
      [ProjectStage.EARLY_DEV]: null,
      [ProjectStage.MID_DEV]: null,
      [ProjectStage.LATE_DEV]: null,
      [ProjectStage.READY_FID]: null,
      [ProjectStage.POST_FID]: null,
      [ProjectStage.POST_FID_FULL]: null,
      [ProjectStage.PRE_CONSTRUCTION]: null,
      [ProjectStage.CONSTRUCTION]: null,
      [ProjectStage.ENERGIZATION_COD]: null,
      [ProjectStage.POST_COD]: null,
      [ProjectStage.OPERATIONS]: null,
    };
    if (stageData.allMilestones.length <= 0) {
      return scheduleData;
    }

    const exampleDef = stageData.allMilestones[0].countryDef;
    const technology = exampleDef.technology;
    const countryCode = exampleDef.countryCode;

    const msPerStage: { [stage: string]: RichMilestone[] } = {};
    for (const ms of scheduleData.allMilestones) {
      msPerStage[ms.stage] = msPerStage[ms.stage] || [];
      msPerStage[ms.stage].push(ms);
    }
    const numberOfUndoneKeyMs: { [stage: string]: number } = {};
    const latestDateOfKeyMs: { [stage: string]: Date } = {};
    for (const stage of scheduleData.stages) {
      numberOfUndoneKeyMs[stage] = msPerStage[stage].filter((ms) => {
        if (isDevelopmentStage(stage)) {
          return ms.key && !ms.na && !ms.done;
        } else if (isConstructionStage(stage)) {
          return !ms.na && !ms.done;
        }

        return false;
      }).length;

      latestDateOfKeyMs[stage] = msPerStage[stage]
        .filter((ms) => this.isRelevantForEndOfStageCalculation(ms))
        .flatMap((ms) => this.getDatesForEndOfStageCalculation(ms))
        .filter((date) => date !== null)
        .reduce((prevDate, newDate) => this.getLatestDate(prevDate, newDate), null);

      const defId = 'endOfStage-' + stage;
      const name = 'End of Stage ' + stageToLabel[stage];

      scheduleData[stage] = {
        milestones: msPerStage[stage],
        endOfStage: {
          id: defId,
          countryDef: {
            id: defId,
            technology: technology,
            index: -1,
            customRef: '',
            commonId: null,
            countryCode: countryCode,
            name: name,
            summary: '',
            stage: stage,
            category: MilestoneCategory.OTHER,
            key: false,
            mandatory: false,
            scheduleTab: true,
            matrixTab: false,
            perfTrackedEu: false,
            perfTrackedCountry: false,
            includeIfHybrid: false,
            includeIfDivestment: false,
            notApplicable: false,
            adfPredecessor: null,
            adfDuration: null,
            responsibleId: null,
            dependencies: [],
          },
          commonDef: null,

          index: -1,
          customRef: '',
          name: name,
          summary: '',
          stage: stage,
          category: MilestoneCategory.OTHER,
          key: false,
          mandatory: false,
          scheduleTab: true,
          perfTrackedEu: false,
          perfTrackedCountry: false,
          includeIfHybrid: false,
          includeIfDivestment: false,
          notApplicable: false,
          synthetic: true,
          construction: false,
          incomingDependencies: [],
          outgoingDependencies: [],
          keyDataChange: 'none',

          date: latestDateOfKeyMs[stage],
          done: numberOfUndoneKeyMs[stage] === 0,
          na: false,
        },
        doneCount: msPerStage[stage].filter((ms) => ms.done || ms.na).length,
      };
    }

    for (const stage of scheduleData.stages) {
      this.sortRichMilestoneData(scheduleData[stage].milestones);
    }

    return scheduleData;
  }

  private static isRelevantForEndOfStageCalculation(ms: RichMilestone): boolean {
    const includeDev =
      !ms.construction && ms.stage !== ProjectStage.OPERATIONS && ms.key && !ms.na && DateUtil.isDate(ms.date);
    const includeCon = ms.construction && (DateUtil.isDate(ms.date) || DateUtil.isDate(ms.dateStart));
    return includeDev || includeCon;
  }

  private static getDatesForEndOfStageCalculation(ms: RichMilestone): Date[] {
    return [
      DateUtil.isDate(ms.date) ? DateUtil.anyToDate(ms.date) : null,
      DateUtil.isDate(ms.dateStart) ? DateUtil.anyToDate(ms.dateStart) : null,
    ];
  }

  private static getLatestDate(a: Date, b: Date): Date {
    return !a || b.getTime() > a.getTime() ? b : a;
  }

  private static filterForDefsOnScheduleTab(milestoneDefinitions: CountryMilestoneDef[]): CountryMilestoneDef[] {
    return milestoneDefinitions
      .filter((msDef) => msDef.stage && msDef.category && msDef.scheduleTab)
      .sort((d1, d2) => d1.index - d2.index);
  }

  private static getMilestonesFiltered(
    ctxt: MilestoneCalcContext,
    projectMilestones: AbstractMilestone[],
    projectSelections: { [selectorId: string]: string },
    milestoneDefinitions: MilestoneDefinitions,
    governanceGroups: GovernanceGroup[],
  ): { ms: AbstractMilestone[]; filtered: Set<string> } {
    const filteredMilestones: Set<string> = new Set<string>();
    const milestones = projectMilestones.slice();
    for (const group of governanceGroups) {
      const selectedOptId = projectSelections[group.id] || group.selector.defaultOptionId;
      this.filterBySelection(milestones, filteredMilestones, milestoneDefinitions, group, selectedOptId);
    }
    this.filterByHybridAndDivestment(ctxt, milestones, filteredMilestones, milestoneDefinitions);
    return { ms: milestones, filtered: filteredMilestones };
  }

  private static filterBySelection(
    out: AbstractMilestone[],
    filtered: Set<string>,
    milestoneDefinitions: MilestoneDefinitions,
    group: GovernanceGroup,
    optionId: string,
  ) {
    const selectedOpt = group.selector.options.find((opt) => opt.id === optionId);
    if (!selectedOpt) {
      console.error(`Can not find Governance Group Option for ID ${optionId} for Governance Group ${group.name}`);
      return;
    }
    const allCommonMsDefIds = group.milestoneCommonIds;
    const toBeKept = selectedOpt.commonMsDefIds;
    for (const defId of allCommonMsDefIds) {
      if (toBeKept.includes(defId)) {
        const needsToBeAdded = out.findIndex((ms) => ms.commonDefId === defId) < 0;
        if (needsToBeAdded) {
          const countryDef = milestoneDefinitions.countryMilestones.find((def) => def.commonId === defId);
          if (countryDef) {
            out.push({
              countryDefId: countryDef.id,
              commonDefId: defId,
              date: null,
              done: false,
              na: false,
            });
          } else {
            console.error(
              `Can not find Country Milestone Definition for Common Milestone Definition ${defId} from Governance Group`,
            );
          }
        }
      } else {
        filtered.add(defId);
        const toBeRemovedIdx = out.findIndex((ms) => ms.commonDefId === defId);
        if (toBeRemovedIdx >= 0) {
          out.splice(toBeRemovedIdx, 1);
        }
      }
    }
  }

  private static filterByHybridAndDivestment(
    ctxt: MilestoneCalcContext,
    out: AbstractMilestone[],
    filtered: Set<string>,
    milestoneDefinitions: MilestoneDefinitions,
  ) {
    for (const def of milestoneDefinitions.countryMilestones) {
      if (!def.includeIfHybrid && !def.includeIfDivestment) {
        // no impact on milestones which have neither checkbox checked; old behaviour kept unchanged
        continue;
      }
      if (filtered.has(def.id)) {
        // already filtered by DoA selectors and they have higher priority
        continue;
      }
      const removeHybrid = def.includeIfHybrid && ctxt.technologies.length === 1;
      const removeDivestment = def.includeIfDivestment && !isDivestment[ctxt.businessModel];
      const shouldNotExist = removeHybrid || removeDivestment;
      if (shouldNotExist) {
        filtered.add(def.id);
      }
    }
  }

  private static addMilestoneToData(
    stageData: RichMilestoneData,
    commonDef: CommonMilestoneDef,
    countryDef: CountryMilestoneDef,
    projectMilestones: AbstractMilestone[],
  ) {
    let ms: AbstractMilestone = projectMilestones.find((milestone) => milestone.countryDefId === countryDef.id);
    if (!ms) {
      ms = {
        countryDefId: countryDef?.id,
        commonDefId: commonDef?.id,
        date: null,
        done: false,
        na: false,
      };
    }
    const category = this.collapseGovernance(countryDef.category);
    this.addIfAbsent(stageData.stages, countryDef.stage);
    this.addIfAbsent(stageData.categories, category);

    if (!stageData.stageAndCategoryToMilestones[countryDef.stage]) {
      stageData.stageAndCategoryToMilestones[countryDef.stage] = {};
    }
    if (!stageData.stageAndCategoryToMilestones[countryDef.stage][category]) {
      stageData.stageAndCategoryToMilestones[countryDef.stage][category] = [];
    }
    const richMilestone: RichMilestone = {
      id: countryDef.id,
      countryDef: countryDef,
      commonDef: commonDef,

      index: countryDef.index,
      customRef: countryDef.customRef,
      name: countryDef.name,
      summary: this.getSummaryFor(countryDef, commonDef),
      stage: countryDef.stage,
      category: category,
      key: countryDef.key,
      mandatory: countryDef.mandatory || countryDef.includeIfDivestment || countryDef.includeIfHybrid,
      scheduleTab: countryDef.scheduleTab,
      perfTrackedEu: countryDef.perfTrackedEu,
      perfTrackedCountry: countryDef.perfTrackedCountry,
      includeIfHybrid: countryDef.includeIfHybrid,
      includeIfDivestment: countryDef.includeIfDivestment,
      notApplicable: countryDef.notApplicable,
      synthetic: false,
      construction: false,
      incomingDependencies: [],
      outgoingDependencies: [],
      keyDataChange: this.calculateKeyDataChangeFlag(commonDef),

      date: ms.date,
      done: ms.done,
      na: ms.na,
    };
    stageData.allMilestones.push(richMilestone);
    stageData.stageAndCategoryToMilestones[countryDef.stage][category].push(richMilestone);
    stageData.countryDefIdToMilestone[countryDef.id] = richMilestone;
  }

  private static addConstructionMilestoneToData(
    id: ConstructionMilestoneId,
    stageData: RichMilestoneData,
    projectMilestones: AbstractMilestone[],
  ) {
    const template: MmsTemplate = MandatoryMilestonesMap.get(id);
    if (!template) {
      console.error('failed to find MmsTemplate for construction milestone with ID ', id);
      return;
    }
    let ms: AbstractMilestone = projectMilestones.find((milestone) => milestone.commonDefId === id);
    if (!ms) {
      ms = {
        countryDefId: id,
        commonDefId: id,
        date: null,
        done: false,
        na: false,
      };
    }
    const stage = idToStage[id];
    const category = MilestoneCategory.CONSTRUCTION;
    this.addIfAbsent(stageData.stages, stage);

    if (!stageData.stageAndCategoryToMilestones[stage]) {
      stageData.stageAndCategoryToMilestones[stage] = {};
    }
    if (!stageData.stageAndCategoryToMilestones[stage][category]) {
      stageData.stageAndCategoryToMilestones[stage][category] = [];
    }
    const richMilestone: RichMilestone = this.toRichConstructionMilestone(id, ms);
    stageData.allMilestones.push(richMilestone);
    stageData.stageAndCategoryToMilestones[stage][category].push(richMilestone);
    stageData.countryDefIdToMilestone[id] = richMilestone;
  }

  public static toRichConstructionMilestone(id: ConstructionMilestoneId, ms: AbstractMilestone): RichMilestone {
    const template: MmsTemplate = MandatoryMilestonesMap.get(id);
    const stage = idToStage[id];
    const category = MilestoneCategory.CONSTRUCTION;
    return {
      id: id,
      countryDef: {
        id: id,
        commonId: id,
        index: 0,
        customRef: '',
        technology: null,
        countryCode: null,
        name: template.name,
        summary: template.tooltip,
        stage: stage,
        category: category,
        key: true,
        mandatory: true,
        scheduleTab: true,
        matrixTab: false,
        perfTrackedEu: false,
        perfTrackedCountry: false,
        includeIfHybrid: false,
        includeIfDivestment: false,
        notApplicable: false,
        adfPredecessor: null,
        adfDuration: 0,
        responsibleId: null,
        dependencies: [],
      },
      commonDef: null,

      index: 0,
      customRef: '',
      name: template.name,
      summary: template.tooltip,
      stage: stage,
      category: category,
      key: true,
      mandatory: true,
      scheduleTab: true,
      perfTrackedEu: false,
      perfTrackedCountry: false,
      includeIfHybrid: false,
      includeIfDivestment: false,
      notApplicable: false,
      synthetic: false,
      construction: true,
      constructionTemplate: template,
      incomingDependencies: [],
      outgoingDependencies: [],
      keyDataChange: id === ConstructionMilestoneId.cod ? 'cod' : 'none',

      date: ms.date,
      done: ms.done,
      na: false,
      dateStart: ms.dateStart,
      dateFid: ms.dateFid,
      dateFidStart: ms.dateFidStart,
      started: ms.started,
    };
  }

  public static createRichMilestoneForConstructionMilestone(ms: ConstructionMilestone): RichMilestone {
    const template = MandatoryMilestonesMap.get(ms.id);
    const stage = ProjectStage.CONSTRUCTION;
    const category = MilestoneCategory.CONSTRUCTION;
    return {
      id: ms.id,
      countryDef: {
        id: ms.id,
        commonId: ms.id,
        index: 0,
        customRef: '',
        technology: null,
        countryCode: null,
        name: template.name,
        summary: template.tooltip,
        stage: stage,
        category: category,
        key: false,
        mandatory: true,
        scheduleTab: true,
        matrixTab: false,
        perfTrackedEu: false,
        perfTrackedCountry: false,
        includeIfHybrid: false,
        includeIfDivestment: false,
        notApplicable: false,
        adfPredecessor: null,
        adfDuration: 0,
        responsibleId: null,
        dependencies: [],
      },
      commonDef: null,

      index: 0,
      customRef: '',
      name: template.name,
      summary: template.tooltip,
      stage: stage,
      category: category,
      key: false,
      mandatory: true,
      scheduleTab: true,
      perfTrackedEu: false,
      perfTrackedCountry: false,
      includeIfHybrid: false,
      includeIfDivestment: false,
      notApplicable: false,
      synthetic: false,
      construction: true,
      constructionTemplate: template,
      incomingDependencies: [],
      outgoingDependencies: [],
      keyDataChange: ms.id === ConstructionMilestoneId.cod ? 'cod' : 'none',

      date: ms.date,
      done: ms.done,
      na: false,
      dateStart: ms.startDate,
      dateFid: ms.fidDate,
      dateFidStart: ms.fidStartDate,
      started: ms.started,
    };
  }

  private static calculateDependentMilestones(milestonesData: RichMilestoneData) {
    for (const ms of milestonesData.allMilestones) {
      ms.incomingDependencies = [];
      ms.outgoingDependencies = [];
      for (const dep of ms.countryDef?.dependencies ?? []) {
        if (
          dep.entityType === CountryMilestoneDependencyEntityType.MILESTONE &&
          isAppliedInDigiRES(dep.appliedInApplication)
        ) {
          const depMs = milestonesData.countryDefIdToMilestone[dep.entityId];
          if (!!depMs) {
            ms.hasIncomingDependencies = true;
            ms.mandatory = false;
            ms.incomingDependencies.push({ dependency: dep, source: depMs });
            depMs.hasOutgoingDependencies = true;
            depMs.outgoingDependencies.push(ms);
          } else {
            console.error(
              `Auto-Updated-Milestones: Dependency '${dep.entityId}' can not be found for milestone '${ms.id}'`,
            );
          }
        }
      }
    }
  }

  private static calculateKeyDataChangeFlag(commonDef: CommonMilestoneDef): 'none' | 'fid' | 'cod' | 'sel' {
    if (!commonDef) {
      return 'none';
    }
    if (commonDef.id.endsWith('_cod')) {
      return 'cod';
    }
    if (commonDef.id.endsWith('_sel')) {
      return 'sel';
    }
    if (commonDef.id.includes('_fid_')) {
      return 'fid';
    }
    return 'none';
  }

  public static collapseGovernance(category: MilestoneCategory): MilestoneCategory {
    if (
      category === MilestoneCategory.GOVERNANCE_T1 ||
      category === MilestoneCategory.GOVERNANCE_T2 ||
      category === MilestoneCategory.GOVERNANCE_T3
    ) {
      return MilestoneCategory.GOVERNANCE_T1;
    }
    return category;
  }

  public static addIfAbsent(collection: any[], element: any) {
    if (!collection.includes(element)) {
      collection.push(element);
    }
  }

  public static getSummaryFor(countryDef: CountryMilestoneDef, commonDef: CommonMilestoneDef): string {
    if (commonDef?.summary && countryDef?.summary) {
      return commonDef.summary + ' ' + countryDef.summary;
    }
    return commonDef?.summary || countryDef?.summary || '';
  }

  public static findSummaryFor(countryDef: CountryMilestoneDef, definitions: MilestoneDefinitions): string {
    if (countryDef.commonId) {
      const commonDef = definitions.commonMilestonesMap[countryDef.commonId];
      return this.getSummaryFor(countryDef, commonDef);
    }
    return countryDef.summary;
  }

  private static calculateCompletion(matrixData: StageMatrixData) {
    let countTotalMs = 0;
    let countCompleteMs = 0;
    for (const category of matrixData.categories) {
      let countTotalMsForCat = 0;
      let countCompleteMsForCat = 0;

      for (const stage of matrixData.stages) {
        const allMsInThisStage = matrixData.stageAndCategoryToMilestones[stage][category];
        if (allMsInThisStage && allMsInThisStage.length > 0) {
          countTotalMsForCat += allMsInThisStage.filter((ms) => ms.scheduleTab && !ms.na).length;

          const completedMilestones = allMsInThisStage.filter((ms) => ms.scheduleTab && ms.date && ms.done && !ms.na);
          countCompleteMsForCat += completedMilestones.length;
        }
      }

      countTotalMs += countTotalMsForCat;
      countCompleteMs += countCompleteMsForCat;

      matrixData.completionByCategory[category] = {
        total: countTotalMsForCat,
        completed: countCompleteMsForCat,
        percent: countTotalMsForCat > 0 ? ((countCompleteMsForCat / countTotalMsForCat) * 100).toFixed(0) : '0',
      };
    }
    matrixData.completionTotal = {
      total: countTotalMs,
      completed: countCompleteMs,
      percent: countTotalMs > 0 ? ((countCompleteMs / countTotalMs) * 100).toFixed(0) : '0',
    };
  }

  private static sortMilestones(milestones: RichMilestone[]): void {
    const maxIdx = milestones.length;
    milestones.sort((a, b) => {
      const idxA = a.synthetic ? 0 : a.index || maxIdx;
      const idxB = b.synthetic ? 0 : b.index || maxIdx;
      return idxA - idxB;
    });
  }

  private static sortRichMilestoneData(milestones: RichMilestone[]): void {
    milestones.sort((ms1, ms2) => {
      const stage1 = ms1.stage || ProjectStage.ORIGINATION;
      const stage2 = ms2.stage || ProjectStage.ORIGINATION;
      const index1 = getStageIndex(stage1);
      const index2 = getStageIndex(stage2);
      if (index1 === index2) {
        const order1 = ms1.index < 0 ? 99999 : ms1.index;
        const order2 = ms2.index < 0 ? 99999 : ms2.index;
        return order1 - order2;
      }
      return index1 - index2;
    });
  }

  private static calculateCurrentStage(phase: ProjectPhase, stageData: StageData): ProjectStage {
    if (phase === ProjectPhase.OPERATIONS) {
      return ProjectStage.OPERATIONS;
    }
    const stagesToCheck = phase === ProjectPhase.DEVELOPMENT ? devStages : conStages;

    let previousStage = stagesToCheck[0];

    for (const stage of stagesToCheck) {
      for (const ms of stageData.allMilestones) {
        if (ms.key && !ms.done && ms.stage === stage && !ms.na) {
          return stage;
        }
      }
      previousStage = stage;
    }

    return previousStage;
  }

  public static createMilestoneEntry(ms: RichMilestone, phase: ProjectPhase, readOnly: boolean): MilestoneData {
    const mandatory = this.isMilestoneMandatory(ms, phase);
    return {
      ms: ms,
      initial: {
        date: DateUtil.formField(ms.date),
        complete: ms.done,
        notApplicable: ms.na,

        dateStart: DateUtil.formField(ms.dateStart),
        dateFid: DateUtil.formField(ms.dateFid),
        dateFidStart: DateUtil.formField(ms.dateFidStart),
        started: ms.started,
      },
      fg: new FormGroup(
        {
          date: new FormControl<Date | string>(DateUtil.formField(ms.date), { validators: [ValidatorUtil.validateDate], updateOn: 'blur' }),
          complete: new FormControl(ms.done),
          notApplicable: new FormControl(ms.na),

          dateStart: new FormControl<Date | string>(DateUtil.formField(ms.dateStart), { validators: [ValidatorUtil.validateDate], updateOn: 'blur' }),
          dateFid: new FormControl<Date | string>(DateUtil.formField(ms.dateFid), { validators: [ValidatorUtil.validateDate], updateOn: 'blur' }),
          dateFidStart: new FormControl<Date | string>(DateUtil.formField(ms.dateFidStart), { validators: [ValidatorUtil.validateDate], updateOn: 'blur' }),
          started: new FormControl(ms.started),
        },
        this.validateMilestone(mandatory, ms.construction && !ms.constructionTemplate.isSingleMilestone),
      ),
      mandatory: mandatory,
    };
  }

  private static isMilestoneMandatory(ms: RichMilestone, phase: ProjectPhase): boolean {
    switch (phase) {
      case ProjectPhase.DEVELOPMENT:
        return !ms.construction && !ms.synthetic && !ms.hasIncomingDependencies && ms.countryDef?.mandatory;
      case ProjectPhase.CONSTRUCTION:
        return ms.construction || (ms.stage === ProjectStage.CONSTRUCTION && ms.countryDef?.mandatory);
      case ProjectPhase.OPERATIONS:
        return ms.stage === ProjectStage.OPERATIONS && ms.countryDef?.mandatory;
    }
    return false;
  }

  public static validateMilestone(mandatory: boolean, hasStartDate: boolean): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const lockCtrl = group.get('notApplicable');
      if (lockCtrl && lockCtrl.value) {
        group.get('date').setErrors(null);
        group.get('dateStart').setErrors(null);
        group.get('dateFid').setErrors(null);
        group.get('dateFidStart').setErrors(null);
        return null;
      }
      this.validateMilestoneDateFormControl(mandatory, group.get('date'));
      this.validateMilestoneDateFormControl(mandatory && hasStartDate, group.get('dateStart'));
      this.validateMilestoneDateFormControl(false, group.get('dateFid'));
      this.validateMilestoneDateFormControl(false, group.get('dateFidStart'));
      return null;
    };
  }

  public static validateMilestoneDateFormControl(mandatory: boolean, formCtrl: AbstractControl): void {
    const errors = ValidatorUtil.createMilestoneValidator(mandatory)(formCtrl);
    formCtrl.setErrors(errors);
  }

  public static findMilestoneChanges(
    before: RichMilestone[],
    after: RichMilestone[],
  ): { added: RichMilestone[]; removed: RichMilestone[] } {
    const addedSet: { [id: string]: RichMilestone } = {}; // milestone in after, not in before
    const removedSet: { [id: string]: RichMilestone } = {}; // milestone in before, not in after
    for (const ms of before) {
      removedSet[ms.id] = ms;
    }
    for (const ms of after) {
      addedSet[ms.id] = ms;
      delete removedSet[ms.id];
    }
    for (const ms of before) {
      delete addedSet[ms.id];
    }

    return {
      added: Object.values(addedSet),
      removed: Object.values(removedSet),
    };
  }
}
